import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Modal from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ShieldCheck,
  ShieldAlert,
  RotateCcw,
  AlertTriangle,
  Calendar,
  Loader2,
  CheckCircle2,
  Check,
  ScanBarcode,
  Printer,
  PenTool,
  ExternalLink,
  ArrowRight,
} from "lucide-react";
import apiClient from "@/api/apiClient";
import warrantyApi from "@/api/warrantyApi";
import { API_ENDPOINTS } from "@/api/header";
import { toast } from "sonner";
import { formatDate } from "@/lib/utils";
import {
  getSaleItemReturnDetails,
  WARRANTY_RETURN_CONDITIONS,
  calculateWarrantyDates,
  getDocumentType,
} from "@/lib/warrantyUtils";
import SignatureCaptureModal from "../warranty/components/SignatureCaptureModal";
import WarrantyDocumentModal from "../warranty/components/WarrantyDocumentModal";

const PosCreateWarrantyClaimModal = ({
  show,
  onHide,
  sale,
  selectedItem,
  saleItems = [],
  onSuccess,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentStore = useSelector((state) => state.current_store);

  // User permissions
  const rawPermissions = useSelector((state) => state.userpermission_data);
  const permissions = React.useMemo(() => rawPermissions || [], [rawPermissions]);
  const hasPermission = React.useCallback(
    (perm) => permissions.includes(perm),
    [permissions],
  );
  const canCreateClaim = hasPermission("warranty claim create");

  // Active item in the claim form
  const [activeItemId, setActiveItemId] = useState("");
  const [defectDescription, setDefectDescription] = useState("");
  const [returnCondition, setReturnCondition] = useState("defective");
  const [serialNumber, setSerialNumber] = useState("");
  const [warrantyStartDate, setWarrantyStartDate] = useState("");
  const [warrantyEndDate, setWarrantyEndDate] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Post-creation intake flow state
  const [createdClaim, setCreatedClaim] = useState(null);
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [docModalOpen, setDocModalOpen] = useState(false);

  const populateItemFields = React.useCallback(
    (item) => {
      if (!item) return;

      // Serial extraction
      let serial = "";
      if (item.inventory_serials?.serial_number) {
        serial = item.inventory_serials.serial_number;
      } else if (
        Array.isArray(item.inventory_serials) &&
        item.inventory_serials[0]?.serial_number
      ) {
        serial = item.inventory_serials[0].serial_number;
      } else if (item.inventory_serial?.serial_number) {
        serial = item.inventory_serial.serial_number;
      } else if (item.serial_number) {
        serial = item.serial_number;
      } else if (Array.isArray(item.serial_numbers) && item.serial_numbers.length > 0) {
        serial = item.serial_numbers[0];
      } else if (typeof item.serial_numbers === "string") {
        try {
          const parsed = JSON.parse(item.serial_numbers);
          if (Array.isArray(parsed) && parsed.length > 0) serial = parsed[0];
          else serial = item.serial_numbers;
        } catch {
          serial = item.serial_numbers;
        }
      }
      setSerialNumber(serial || "");

      // Dates extraction (YYYY-MM-DD): default from item > sale date > today
      const rawStart =
        item.warranty_start_date ||
        sale?.created_at ||
        item.created_at ||
        new Date().toISOString();
      const startStr = rawStart
        ? rawStart.split("T")[0]
        : new Date().toISOString().split("T")[0];
      setWarrantyStartDate(startStr);

      let endStr = item.warranty_end_date
        ? item.warranty_end_date.split("T")[0]
        : "";

      const prodCandidate = item.product || item.product_data;
      const dates = calculateWarrantyDates(prodCandidate, startStr);

      if (!endStr) {
        endStr = dates.endDate;
      }
      setWarrantyEndDate(endStr);
    },
    [sale?.created_at],
  );

  // Synchronize active item when modal opens or selectedItem changes
  useEffect(() => {
    if (!show) {
      setCreatedClaim(null);
      setSignModalOpen(false);
      return;
    }

    const initialItem =
      selectedItem || (saleItems && saleItems.length > 0 ? saleItems[0] : null);

    if (initialItem) {
      setActiveItemId(String(initialItem.id));
      populateItemFields(initialItem);
    } else {
      setActiveItemId("");
      setSerialNumber("");
      setWarrantyStartDate("");
      setWarrantyEndDate("");
    }

    setDefectDescription("");
    setReturnCondition("defective");
    setNotes("");
    setCreatedClaim(null);
  }, [show, selectedItem, saleItems, populateItemFields]);

  // Find currently selected item object
  const currentItem =
    saleItems?.find((i) => String(i.id) === String(activeItemId)) ||
    selectedItem ||
    (saleItems && saleItems[0]);

  const returnDetails = getSaleItemReturnDetails(currentItem);

  // A product can only be tracked by serial OR batch at one time, never both
  const itemTrackingType = React.useMemo(() => {
    if (!currentItem) return "none";
    const prod = currentItem.product || currentItem.product_data;
    if (prod?.tracking_type === "serial" || Boolean(prod?.is_serialized) || serialNumber) {
      return "serial";
    }
    if (
      prod?.tracking_type === "standard" ||
      currentItem.inventory_batch_id ||
      currentItem.batch_id ||
      currentItem.batch_number ||
      currentItem.inventory_batch?.batch_number
    ) {
      return "batch";
    }
    if (prod?.tracking_type === "none") {
      return "none";
    }
    return "none";
  }, [currentItem, serialNumber]);

  const isSerialItem = itemTrackingType === "serial";
  const isBatchItem = itemTrackingType === "batch";

  const batchNumber =
    currentItem?.inventory_batch?.batch_number ||
    currentItem?.batch_number ||
    currentItem?.batch?.batch_number ||
    "";

  const coverageInfo = React.useMemo(() => {
    if (!currentItem) return null;
    const prodCandidate = currentItem.product || currentItem.product_data;
    return calculateWarrantyDates(prodCandidate, warrantyStartDate || new Date());
  }, [currentItem, warrantyStartDate]);

  const isNoWarranty =
    coverageInfo?.isNoWarranty ||
    currentItem?.product?.warranty === 0 ||
    currentItem?.product?.warranty_timeframe === "No Warranty" ||
    (warrantyStartDate && warrantyEndDate && warrantyStartDate === warrantyEndDate);

  const handleItemSelectChange = (itemId) => {
    setActiveItemId(itemId);
    const item = saleItems?.find((i) => String(i.id) === String(itemId));
    if (item) {
      populateItemFields(item);
    }
  };

  const getWarrantyValidityBadge = (item) => {
    const retInfo = getSaleItemReturnDetails(item);
    if (retInfo.isReturned) {
      return (
        <Badge
          variant="outline"
          className="text-[10px] py-0 h-4 text-amber-700 dark:text-amber-400 border-amber-500/40 bg-amber-500/10 font-semibold gap-1"
          title={retInfo.reason}
        >
          <RotateCcw className="h-2.5 w-2.5 text-amber-600 dark:text-amber-400" />
          Already Returned ({retInfo.label})
        </Badge>
      );
    }

    if (isNoWarranty) {
      return (
        <Badge
          variant="outline"
          className="text-[10px] py-0 h-4 text-amber-700 dark:text-amber-400 border-amber-500/30 bg-amber-500/10 font-medium"
        >
          No Warranty
        </Badge>
      );
    }

    const endDateStr = warrantyEndDate || item?.warranty_end_date;
    if (!endDateStr) {
      return (
        <Badge variant="outline" className="text-[10px] py-0 h-4 text-muted-foreground">
          No Warranty Info
        </Badge>
      );
    }
    const end = new Date(endDateStr);
    const now = new Date();
    if (end >= now) {
      return (
        <Badge
          variant="outline"
          className="text-[10px] py-0 h-4 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 bg-emerald-500/10 font-medium"
        >
          Active (until {formatDate(endDateStr)})
        </Badge>
      );
    }
    return (
      <Badge
        variant="outline"
        className="text-[10px] py-0 h-4 text-rose-600 dark:text-rose-400 border-rose-500/30 bg-rose-500/10 font-medium"
      >
        Expired ({formatDate(endDateStr)})
      </Badge>
    );
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!canCreateClaim) {
      toast.error("You do not have permission ('warranty claim create') to create warranty claims");
      return;
    }
    if (!sale) {
      toast.error("No sale record found for this claim");
      return;
    }
    if (!currentItem) {
      toast.error("Please select a product item from this sale");
      return;
    }
    if (returnDetails.isReturned) {
      toast.error(
        `Cannot submit claim: This item is already marked as ${returnDetails.label} (${returnDetails.reason}).`,
      );
      return;
    }

    const customerId = sale.customer_id || sale.customer?.id;
    if (!customerId) {
      toast.error("Customer information is required for warranty claims");
      return;
    }

    const productId = currentItem.product_id || currentItem.product?.id;
    if (!productId) {
      toast.error("Product information is required for warranty claims");
      return;
    }

    const storeId = sale.store_id || currentStore?.id;
    if (!storeId) {
      toast.error("Store information is required for warranty claims");
      return;
    }

    const itemDesc = (
      currentItem.product?.name ||
      currentItem.product_data?.name ||
      currentItem.name ||
      "Warranty Claim Item"
    ).trim().slice(0, 255);

    if (!defectDescription.trim()) {
      toast.error("Please provide a description of the defect or problem");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        customer_id: customerId,
        product_id: productId,
        store_id: storeId,
        sale_id: sale.id || null,
        sale_item_id: currentItem.sale_item_id || currentItem.id || null,
        item_description: itemDesc,
        defect_description: notes.trim()
          ? `${defectDescription.trim()}\n\nNotes: ${notes.trim()}`
          : defectDescription.trim(),
        return_condition: returnCondition || "defective",
        serial_number: isSerialItem && serialNumber.trim()
          ? serialNumber.trim().slice(0, 255)
          : null,
        inventory_batch_id: isBatchItem
          ? (currentItem.inventory_batch_id || currentItem.batch_id || null)
          : null,
        warranty_start_date: warrantyStartDate || null,
        warranty_end_date: warrantyEndDate || null,
      };

      const res = await apiClient.post(API_ENDPOINTS.WARRANTY.CLAIMS, payload);
      const claim = res.data?.data || res.data;

      toast.success(
        `Warranty claim #${claim?.claim_number || claim?.id || "NEW"} created successfully!`,
      );

      // Invalidate queries so lists update everywhere
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });

      if (onSuccess) {
        onSuccess(claim, currentItem);
      }

      // Transition to next-steps view
      setCreatedClaim(claim);
    } catch (err) {
      console.error("Submit warranty claim error:", err);
      toast.error(
        err.response?.data?.message || "Failed to submit warranty claim",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = () => {
    setCreatedClaim(null);
    setSignModalOpen(false);
    setDocModalOpen(false);
    onHide();
  };

  const handleSignatureDone = (data) => {
    const isSigned = Boolean(data);
    if (isSigned) {
      toast.success("Customer signature captured on Intake Receipt!");
    } else {
      toast.info("Digital signature removed. Document ready for physical signing.");
    }
    setCreatedClaim((prev) => {
      if (!prev) return prev;
      const docs = Array.isArray(prev.documents) ? [...prev.documents] : [];
      const idx = docs.findIndex((d) => getDocumentType(d) === "intake");
      if (idx >= 0) {
        docs[idx] = { ...docs[idx], is_signed: isSigned };
      }
      return { ...prev, documents: docs };
    });
  };

  const customerName =
    sale?.customer?.full_name ||
    sale?.customer?.name ||
    sale?.customer?.customer_name ||
    "Walk-in Customer";

  const intakeDoc =
    Array.isArray(createdClaim?.documents)
      ? createdClaim.documents.find((d) => getDocumentType(d) === "intake")
      : { type: "intake" };

  return (
    <>
      <Modal
        open={show}
        onOpenChange={(v) => !v && handleDone()}
        title={
          createdClaim ? (
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                <CheckCircle2 className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-foreground truncate">
                  Claim #{createdClaim.claim_number || createdClaim.id} Registered
                </h3>
                <p className="text-xs text-muted-foreground font-normal truncate">
                  Intake receipt issued • Customer receipt ready
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <ShieldCheck className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-foreground truncate">
                  Create Warranty Claim
                </h3>
                <p className="text-xs text-muted-foreground font-normal truncate">
                  Sale #{sale?.invoice_number || sale?.id} • {customerName}
                </p>
              </div>
            </div>
          )
        }
        maxWidth="max-w-lg"
        footer={
          createdClaim ? (
            <div className="flex justify-between items-center w-full">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  handleDone();
                  navigate(`/warranty-claims/${createdClaim.id}`, {
                    state: { isNewClaim: true },
                  });
                }}
              >
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                View Full Claim Details
              </Button>
              <Button
                type="button"
                size="sm"
                className="bg-primary text-primary-foreground font-medium"
                onClick={handleDone}
              >
                <Check className="mr-1.5 h-3.5 w-3.5" />
                Return to POS
              </Button>
            </div>
          ) : (
            <div className="flex justify-between items-center w-full">
              <span className="text-[11px] text-muted-foreground">
                Claim will be linked directly to Sale #{sale?.invoice_number || sale?.id}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onHide}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSubmit}
                  disabled={isSubmitting || returnDetails.isReturned || !canCreateClaim}
                  className={
                    returnDetails.isReturned || !canCreateClaim
                      ? "bg-muted text-muted-foreground cursor-not-allowed opacity-60 border border-dashed"
                      : "bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
                  }
                >
                  {isSubmitting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  ) : returnDetails.isReturned ? (
                    <ShieldAlert className="h-4 w-4 mr-1.5 text-amber-600" />
                  ) : (
                    <ShieldCheck className="h-4 w-4 mr-1.5" />
                  )}
                  {!canCreateClaim
                    ? "Permission Required"
                    : returnDetails.isReturned
                      ? "Item Already Claimed"
                      : "Submit Claim"}
                </Button>
              </div>
            </div>
          )
        }
      >
        {createdClaim ? (
          /* SUCCESS INTAKE CONFIRMATION VIEW */
          <div className="space-y-4 py-1">
            <div className="p-4 rounded-xl border border-emerald-500/40 bg-emerald-500/10 text-emerald-950 dark:text-emerald-200 space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-full text-emerald-600 dark:text-emerald-400 shrink-0">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-foreground">
                    Warranty Claim Intake Recorded
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Intake document was generated automatically. Hand over receipt or collect customer signature below.
                  </p>
                </div>
              </div>
            </div>

            {/* Details Summary Card */}
            <Card className="border shadow-none">
              <CardContent className="p-4 space-y-3 text-xs">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pb-3 border-b">
                  <div>
                    <span className="text-muted-foreground block text-[11px]">Customer</span>
                    <span className="font-semibold text-foreground">{customerName}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[11px]">Original Invoice</span>
                    <span className="font-mono font-medium text-foreground">
                      #{sale?.invoice_number || sale?.id}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[11px]">Product</span>
                    <span className="font-medium text-foreground truncate block">
                      {currentItem?.product?.name || "Warranty Item"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[11px]">Serial Number</span>
                    <span className="font-mono font-medium text-foreground">
                      {serialNumber || "Non-serialized"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[11px]">Return Condition</span>
                    <span className="font-medium capitalize text-foreground">
                      {returnCondition.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[11px]">Intake Document</span>
                    <Badge
                      variant={intakeDoc?.is_signed ? "success" : "outline"}
                      className="text-[10px] h-4 py-0"
                    >
                      {intakeDoc?.is_signed ? "Signed" : "Unsigned"}
                    </Badge>
                  </div>
                </div>

                <div>
                  <span className="text-muted-foreground block text-[11px] mb-1 font-medium">
                    Reported Defect:
                  </span>
                  <div className="p-2.5 rounded bg-muted/40 border text-foreground leading-relaxed text-xs">
                    {defectDescription}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <Button
                type="button"
                variant="outline"
                className="h-10 text-xs font-semibold gap-2 border-primary/30 text-primary hover:bg-primary/5"
                onClick={() => setDocModalOpen(true)}
              >
                <Printer className="h-4 w-4" />
                Print / Download Intake Receipt
              </Button>

              <Button
                type="button"
                variant="outline"
                className={`h-10 text-xs font-semibold gap-2 ${intakeDoc?.is_signed
                  ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                  : "border-primary/30 text-primary hover:bg-primary/5"
                  }`}
                onClick={() => setSignModalOpen(true)}
              >
                <PenTool className="h-4 w-4" />
                {intakeDoc?.is_signed
                  ? "Re-sign Intake Receipt"
                  : "Capture Customer Signature"}
              </Button>
            </div>
          </div>
        ) : (
          /* FORM VIEW */
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Multiple items selector (only when sale has more than 1 item) */}
            {saleItems && saleItems.length > 1 && (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Select Item to Claim</Label>
                <Select
                  value={String(activeItemId)}
                  onValueChange={handleItemSelectChange}
                >
                  <SelectTrigger className="h-9 text-xs bg-background">
                    <SelectValue placeholder="Choose product from this sale" />
                  </SelectTrigger>
                  <SelectContent>
                    {saleItems.map((item, idx) => {
                      const itemRet = getSaleItemReturnDetails(item);
                      return (
                        <SelectItem key={item.id || idx} value={String(item.id)} className="text-xs">
                          <div className="flex items-center gap-1.5">
                            <span>
                              {item.product?.name || item.product_data?.name || item.name || `Item #${idx + 1}`} (Qty: {item.quantity || 1})
                            </span>
                            {itemRet.isReturned && (
                              <span className="text-[10px] text-amber-600 font-semibold">
                                • [{itemRet.label}]
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Compact Item Summary Strip (Clean, single-line overview) */}
            {currentItem && (
              <div className="p-3 rounded-lg border bg-muted/20 flex items-center justify-between gap-3 text-xs">
                <div className="min-w-0 space-y-0.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-foreground text-sm truncate">
                      {currentItem.product?.name || currentItem.product_data?.name || currentItem.name || "Item"}
                    </span>
                    {isSerialItem && serialNumber && (
                      <span className="font-mono text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        SN: {serialNumber}
                      </span>
                    )}
                    {isBatchItem && batchNumber && (
                      <span className="font-mono text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        Batch: {batchNumber}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                    {currentItem.product?.sku && <span>SKU: {currentItem.product.sku}</span>}
                    {warrantyEndDate && (
                      <>
                        <span>•</span>
                        <span>Warranty till {formatDate(warrantyEndDate)}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="shrink-0">
                  {getWarrantyValidityBadge(currentItem)}
                </div>
              </div>
            )}

            {/* Warning Alert Banner if Item is Already Returned */}
            {returnDetails.isReturned && (
              <div className="p-3.5 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200 flex items-start gap-2.5 text-xs shadow-xs">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-amber-950 dark:text-amber-100 text-xs">
                      Item Already Processed For Warranty Return
                    </span>
                    <Badge
                      variant="outline"
                      className="text-[9px] py-0 h-4 bg-amber-500/20 text-amber-800 dark:text-amber-300 border-amber-500/40 font-semibold"
                    >
                      Status: {returnDetails.label}
                    </Badge>
                  </div>
                  <p className="text-[11px] text-amber-800 dark:text-amber-300 leading-relaxed">
                    This item ({returnDetails.serialNumber ? `Serial #${returnDetails.serialNumber}` : currentItem?.product?.name || "Item"}) has already been recorded with return status <strong>{returnDetails.label}</strong> ({returnDetails.reason}). Duplicate warranty claims cannot be submitted for the same item.
                  </p>
                </div>
              </div>
            )}

            {/* Item Sold Without Warranty Note */}
            {!returnDetails.isReturned && isNoWarranty && (
              <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2">
                <ShieldAlert className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <span>
                  <strong>Item Sold Without Warranty:</strong> This item carries No Warranty coverage. Claim intake will be accepted, but approving a free replacement or repair will require manager authorization.
                </span>
              </div>
            )}

            {/* Permission warning if user cannot create warranty claim */}
            {!canCreateClaim && (
              <div className="p-3 rounded-lg border border-destructive/30 bg-destructive/10 text-xs text-destructive flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>
                  You do not have permission ('warranty claim create') to register warranty claims.
                </span>
              </div>
            )}

            {/* Actionable Form Fields */}
            <div className="space-y-3 pt-0.5">
              {/* Return Condition */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Return Condition *</Label>
                <Select value={returnCondition} onValueChange={setReturnCondition}>
                  <SelectTrigger className="h-9 text-xs bg-background">
                    <SelectValue placeholder="Select return condition" />
                  </SelectTrigger>
                  <SelectContent>
                    {WARRANTY_RETURN_CONDITIONS.map((cond) => (
                      <SelectItem key={cond.value} value={cond.value} className="text-xs">
                        {cond.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Defect Description */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  Defect Description *
                </Label>
                <Textarea
                  required
                  rows={3}
                  value={defectDescription}
                  onChange={(e) => setDefectDescription(e.target.value)}
                  placeholder="Detailed description of the customer's reported issue, defect observed, or malfunction..."
                  className="text-xs bg-background resize-none"
                />
              </div>

              {/* Notes */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  Internal Notes <span className="text-muted-foreground font-normal">(Optional)</span>
                </Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional technician observations, packaging status, accessories..."
                  className="h-9 text-xs"
                />
              </div>
            </div>
          </form>
        )}
      </Modal>

      {/* Signature Capture Modal */}
      {createdClaim && (
        <SignatureCaptureModal
          open={signModalOpen}
          onOpenChange={setSignModalOpen}
          claim={createdClaim}
          documentObj={intakeDoc}
          onDone={handleSignatureDone}
        />
      )}

      {/* Document Print/Download Modal */}
      {createdClaim && (
        <WarrantyDocumentModal
          open={docModalOpen}
          onOpenChange={setDocModalOpen}
          documentTitle="Warranty Intake Receipt"
          claimNumber={createdClaim.claim_number || createdClaim.id}
          url={warrantyApi.pdf.intake(createdClaim.id)}
          filename={`Intake_Receipt_${createdClaim.claim_number || createdClaim.id}.pdf`}
        />
      )}
    </>
  );
};

PosCreateWarrantyClaimModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  sale: PropTypes.object,
  selectedItem: PropTypes.object,
  saleItems: PropTypes.array,
  onSuccess: PropTypes.func,
};

export default PosCreateWarrantyClaimModal;
